import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  connectAuthEmulator, // Import connectAuthEmulator
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  sendPasswordResetEmail, 
  signOut,
  updateProfile,
  updateEmail,
  updatePassword,
  confirmPasswordReset,
  verifyPasswordResetCode,
  User as FirebaseUser
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore'; // Import connectFirestoreEmulator
import { getAnalytics, isSupported } from 'firebase/analytics';
import { MemberStatus } from '@/types/schema';

// Check if environment variables are available
const checkRequiredEnvVars = () => {
  const requiredVars = [
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_AUTH_DOMAIN',
    'VITE_FIREBASE_PROJECT_ID'
  ];
  
  const missing = requiredVars.filter(varName => !import.meta.env[varName]);
  
  if (missing.length > 0) {
    console.error(`Missing required environment variables: ${missing.join(', ')}`);
    console.error('Please check your .env file and make sure it contains all required Firebase configuration.');
    
    // For development, provide clear instructions
    if (import.meta.env.DEV) {
      console.info('To fix this issue:');
      console.info('1. Create or update the .env file in the project root');
      console.info('2. Add the required Firebase configuration variables');
      console.info('3. Restart your development server');
    }
    
    throw new Error('Firebase configuration is incomplete. See console for details.');
  }
};

// Call the check function
checkRequiredEnvVars();

export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase with error handling
let app;
let auth;
let defaultDb;
let db;

try {
  // Initialize Firebase
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  
  // Initialize Firestore with the specific database
  defaultDb = getFirestore(app);
  
  db = getFirestore(app)

  // Connect to emulators in development
  if (import.meta.env.DEV) {
    // Only connect to emulators if VITE_USE_FIREBASE_EMULATORS is not 'false'
    // Defaults to using emulators in DEV mode if the variable is not set or is 'true'
    if (import.meta.env.VITE_USE_FIREBASE_EMULATORS !== 'false') {
      try {
        console.log('Connecting to Firebase Emulators (VITE_USE_FIREBASE_EMULATORS is not "false")...');
        connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
        connectFirestoreEmulator(db, 'localhost', 8080);
        // If defaultDb is used elsewhere for emulator-specific connections, connect it too.
        // connectFirestoreEmulator(defaultDb, 'localhost', 8080); 
        console.log('Successfully connected to Auth and Firestore Emulators.');
      } catch (e) {
        console.error('Error connecting to Firebase Emulators:', e);
      }
    } else {
      console.log('Skipping emulator connection: VITE_USE_FIREBASE_EMULATORS is set to "false". Using cloud Firebase services.');
    }
  }
  
  console.log('Firebase initialized successfully');
} catch (error) {
  console.error('Error initializing Firebase:', error);
  throw error; // Rethrow to make sure the app doesn't proceed with invalid Firebase
}

// Helper to check for query param to force analytics
function isAnalyticsForced() {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('firebaseAnalytics') === 'true';
  } catch {
    return false;
  }
}

let analytics;
isSupported()
  .then((yes) => {
    const isProduction =
      import.meta.env.NODE_ENV === 'production' ||
      (!import.meta.env.NODE_ENV && import.meta.env.VITE_VERCEL_TARGET_ENV === 'production');

    const shouldEnableAnalytics = isProduction || isAnalyticsForced();

    if (yes && shouldEnableAnalytics) {
      analytics = getAnalytics(app);
    } else {
      // Removed console.warn for analytics not initialized
    }
  })
  .catch((error) => {
    console.error('Error checking Firebase Analytics support:', error);
  });


// Sign in with email and password
export const signIn = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { user: userCredential.user, error: null };
  } catch (error: any) {
    return { user: null, error: error.message || 'Sign in failed' };
  }
};

// Sign up with email and password
export const signUp = async (email: string, password: string, displayName: string) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(userCredential.user, { displayName });
    return { user: userCredential.user, error: null };
  } catch (error: any) {
    return { user: null, error: error.message || 'Sign up failed' };
  }
};

// Send password reset email
export const resetPassword = async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true, error: null };
  } catch (error: any) {
    return { success: false, error: error.message || 'Password reset failed' };
  }
};

// Sign out
export const logOut = async () => {
  try {
    await signOut(auth);
    return { success: true, error: null };
  } catch (error: any) {
    return { success: false, error: error.message || 'Sign out failed' };
  }
};

// Update profile
export const updateUserProfile = async (user: FirebaseUser, profileData: {
  displayName?: string;
  photoURL?: string;
}) => {
  try {
    await updateProfile(user, profileData);
    return { success: true, error: null };
  } catch (error: any) {
    return { success: false, error: error.message || 'Profile update failed' };
  }
};

// Update email
export const updateUserEmail = async (user: FirebaseUser, email: string) => {
  try {
    await updateEmail(user, email);
    return { success: true, error: null };
  } catch (error: any) {
    return { success: false, error: error.message || 'Email update failed' };
  }
};

// Update password
export const updateUserPassword = async (user: FirebaseUser, password: string) => {
  try {
    await updatePassword(user, password);
    return { success: true, error: null };
  } catch (error: any) {
    return { success: false, error: error.message || 'Password update failed' };
  }
};

// Helper to extract basic user info from Firebase user
export const extractUserInfo = (user: any) => {
  return {
    id: user.uid,
    email: user.email || '',
    firstName: user.displayName ? user.displayName.split(' ')[0] : '',
    lastName: user.displayName ? user.displayName.split(' ').slice(1).join(' ') : '',
    phone: user.phoneNumber || '',
    // Include role but as undefined so it can be properly set by other functions
    // This allows the property to exist for TypeScript but doesn't override existing roles
    role: undefined,
    avatarUrl: user.photoURL || '',
    avatarInitials: user.displayName 
      ? `${user.displayName.split(' ')[0][0] || ''}${user.displayName.split(' ')[1]?.[0] || ''}`
      : user.email?.substring(0, 2).toUpperCase() || 'U',
    permissions: [], // Add empty permissions array
    status: 'active' as MemberStatus  // Explicitly cast as MemberStatus
  };
};

// Helper to get initials from a name
const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase();
};

// Verify the password reset code
export const verifyResetCode = async (code: string) => {
  try {
    await verifyPasswordResetCode(auth, code);
    return { valid: true, error: null };
  } catch (error: any) {
    return { 
      valid: false, 
      error: error.message || 'Invalid or expired reset code' 
    };
  }
};

// Confirm password reset
export const confirmReset = async (code: string, newPassword: string) => {
  try {
    await confirmPasswordReset(auth, code, newPassword);
    return { success: true, error: null };
  } catch (error: any) {
    return { 
      success: false, 
      error: error.message || 'Failed to reset password' 
    };
  }
};

export { db, defaultDb , analytics };
export default auth;
